// Pure, framework-free helpers for reading a JobSyncConfig out of a Company's `atsIntegration` blob
// and writing an edited one back — the join with the highest round-trip risk, kept out of React.
//
// The config is nested inside the matching `atsIntegration` array entry under `jobSyncConfig`, keyed
// by the entry whose `ATSName === config.erinConnect.type`. No schema change; `atsIntegration` is an
// AWSJSON string on the wire. This mirrors process-jobs/scripts/seed-peraton-config.ts exactly, except
// it never mutates its input (the seed patches the array in place; the UI must not).

// Local, dependency-free "parse if it's a JSON string, else pass through" — so these modules stay
// importable under plain jest / tsx with no app bootstrap. Mirrors _shared/services/utils.js `parse`.
function parseMaybe(value) {
	if (typeof value !== 'string') return value;
	try {
		return JSON.parse(value);
	} catch {
		return value;
	}
}

// Structural deep-clone. Configs are pure JSON (no undefined/functions/dates), so JSON round-trip is
// exact — and it also strips any accidental `undefined`, which we never want in the emitted JSON.
export function cloneConfig(config) {
	return JSON.parse(JSON.stringify(config));
}

// List the ATS entries that could host a job-sync config, with whether one already exists.
// (ATSName may repeat across entries; the first match wins on read/write, matching the seed.)
export function listAtsEntries(atsIntegration) {
	const arr = parseMaybe(atsIntegration);
	if (!Array.isArray(arr)) return [];
	return arr
		.filter((e) => e && e.ATSName)
		.map((e) => ({ atsName: e.ATSName, hasConfig: e.jobSyncConfig != null }));
}

// Read the jobSyncConfig for a given ATSName. Defensive: `atsIntegration` may arrive as a parsed
// array or an AWSJSON string, and a nested `jobSyncConfig` may be an object or a stringified object.
export function readConfigFromAtsIntegration(atsIntegration, atsName) {
	const arr = parseMaybe(atsIntegration);
	if (!Array.isArray(arr)) return null;
	const entry = arr.find((e) => e && e.ATSName === atsName);
	if (!entry || entry.jobSyncConfig == null) return null;
	const cfg = parseMaybe(entry.jobSyncConfig);
	return cfg && typeof cfg === 'object' ? cfg : null;
}

// Patch ONLY the matching entry's jobSyncConfig on a FRESH atsIntegration array, preserving every
// other entry and the matched entry's other keys (credentials, hiring manager, …) verbatim. Never
// mutates the input; throws when there is no matching entry (we never auto-create — that needs the
// credentials/ATSCreatedById the existing ATS form collects). Returns a NEW array (still a JS value;
// the caller JSON.stringifies it for the updateCompany/updateATSIntegration mutation).
export function writeConfigIntoAtsIntegration(atsIntegration, atsName, config) {
	const arr = parseMaybe(atsIntegration);
	if (!Array.isArray(arr)) throw new Error('atsIntegration is not an array');
	const idx = arr.findIndex((e) => e && e.ATSName === atsName);
	if (idx === -1) {
		throw new Error(`No atsIntegration entry with ATSName="${atsName}". Create the integration first.`);
	}
	return arr.map((entry, i) => (i === idx ? { ...entry, jobSyncConfig: cloneConfig(config) } : entry));
}

// A minimal VALID config for the create flow (entry exists but has no jobSyncConfig yet). The operator
// fills in the rest. Convention preserved: organizationId == companyId; erinConnect.type == the entry's
// ATSName; starts in the safe dry-run mode.
export function scaffoldConfig(entry, companyId) {
	const atsName = entry && entry.ATSName ? entry.ATSName : '';
	return {
		version: 1,
		companyId,
		organizationId: companyId,
		erinConnect: { type: atsName },
		mode: 'dry-run',
		match: { incomingKey: 'externalJobId', erinKeys: ['externalJobId'], normalize: ['trim'] },
		reconciliation: { create: true, update: true, updateSkipUnchanged: true, closeMissing: false },
		fieldMappings: {},
		ruleSets: {},
		bonus: { rules: [], default: { literal: 'No Bonus' } },
		defaults: { status: 'open' },
	};
}

export function isDirty(config, initialJson) {
	return JSON.stringify(config) !== initialJson;
}

// Validation surface. Returns [] when valid, else [{ path, message }]. Mirrors the runtime's hard
// requirements so a saved config can't silently break the Lambda.
export function validateConfig(config) {
	const errors = [];
	if (!config || typeof config !== 'object') {
		return [{ path: '', message: 'Config is missing.' }];
	}
	if (config.mode !== 'dry-run' && config.mode !== 'live') {
		errors.push({ path: 'mode', message: 'Mode must be Dry-run or Live.' });
	}
	if (!config.companyId) errors.push({ path: 'companyId', message: 'companyId is required.' });
	if (!config.erinConnect || !config.erinConnect.type) {
		errors.push({ path: 'erinConnect.type', message: 'Integration type is required.' });
	}
	const match = config.match || {};
	if (!match.incomingKey) errors.push({ path: 'match.incomingKey', message: 'A match incoming key is required.' });
	if (!Array.isArray(match.erinKeys) || match.erinKeys.length === 0) {
		errors.push({ path: 'match.erinKeys', message: 'At least one Erin match key is required.' });
	}
	// Close-missing closes every absent job by default; `closeSource` is an opt-in narrowing, so an
	// omitted key is legal. A key that is present but empty is the one illegal state: the editor
	// shows the restriction as on, while the worker reads '' as falsy and closes the whole company.
	const rec = config.reconciliation || {};
	if (rec.closeMissing && Object.prototype.hasOwnProperty.call(rec, 'closeSource') && !String(rec.closeSource || '').trim()) {
		errors.push({
			path: 'reconciliation.closeSource',
			message: 'Enter a source, or turn off “Only close jobs from one source”.',
		});
	}
	// Dangling { ruleSet } references in fieldMappings.
	const ruleSetNames = new Set(Object.keys(config.ruleSets || {}));
	for (const [target, mapping] of Object.entries(config.fieldMappings || {})) {
		if (mapping && Object.prototype.hasOwnProperty.call(mapping, 'ruleSet') && !ruleSetNames.has(mapping.ruleSet)) {
			errors.push({ path: `fieldMappings.${target}`, message: `References unknown rule set “${mapping.ruleSet}”.` });
		}
	}
	// Every regex condition must compile (the Lambda does `new RegExp(value)` at runtime).
	const ruleSets = [...Object.values(config.ruleSets || {})];
	if (config.bonus) ruleSets.push(config.bonus);
	for (const rs of ruleSets) {
		for (const rule of rs.rules || []) {
			for (const cond of rule.when || []) {
				if (cond.operator === 'regex' && cond.value != null) {
					try {
						// eslint-disable-next-line no-new
						new RegExp(String(cond.value));
					} catch {
						errors.push({ path: 'regex', message: `Invalid regular expression “${cond.value}”.` });
					}
				}
			}
		}
	}
	return errors;
}
